import { useCallback, useEffect, useRef } from "react";
import { FILTER_DEBOUNCE_DELAY } from "../constant";

/**
 * Custom hook for debounced MUI DataGrid filter changes
 * Reduces API calls by debouncing filter updates with configurable delay
 * 
 * @param {Function} setFilterModel - Function to update filter model state
 * @param {number} delay - Debounce delay in milliseconds (default: FILTER_DEBOUNCE_DELAY)
 * @returns {Function} onFilterChange - Callback to be passed to onFilterModelChange
 * 
 * @example
 * const { filterModel, setFilterModel } = usePageState();
 * const onFilterChange = useDebouncedFilter(setFilterModel);
 * 
 * <DataGrid
 *   filteringMode="server"
 *   onFilterModelChange={onFilterChange}
 * />
 */
const useDebouncedFilter = (setFilterModel, delay = FILTER_DEBOUNCE_DELAY) => {
  const filterDebounceTimer = useRef(null);

  const onFilterChange = useCallback((newFilterModel) => {
    // Clear any existing timer to reset the debounce countdown
    if (filterDebounceTimer.current) {
      clearTimeout(filterDebounceTimer.current);
    }
    
    // If filters are being cleared (no items), apply immediately
    if (!newFilterModel.items || newFilterModel.items.length === 0) {
      setFilterModel(newFilterModel);
      return;
    }
    
    // Set a new timer to apply the filter after delay
    filterDebounceTimer.current = setTimeout(() => {
      setFilterModel(newFilterModel);
    }, delay);
  }, [setFilterModel, delay]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (filterDebounceTimer.current) {
        clearTimeout(filterDebounceTimer.current);
      }
    };
  }, []);

  return onFilterChange;
};

export default useDebouncedFilter;

